let nextDifferenceId = 0;

/** A compact, touch and keyboard accessible comparison without changing table height. */
export function mountRevisionDifference(
  root: HTMLFormElement,
  host: HTMLElement,
  comparison: { previousValue: string; currentValue: string; removed: boolean },
) {
  const trigger = document.createElement("span");
  trigger.className = "shift-revision-diff-trigger";
  trigger.tabIndex = 0;
  trigger.role = "button";
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("width", "16");
  icon.setAttribute("height", "16");
  icon.setAttribute("fill", "none");
  icon.setAttribute("stroke", "currentColor");
  icon.setAttribute("stroke-width", "2");
  icon.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", comparison.removed ? "M5 12h14" : "m8 3-4 4 4 4M4 7h16m-4 6 4 4-4 4M20 17H4");
  icon.append(path);
  trigger.append(icon);
  trigger.setAttribute("aria-label", comparison.removed ? "Kaldırılan alanı karşılaştır" : "Eski ve güncel değeri karşılaştır");
  trigger.setAttribute("aria-expanded", "false");
  const popup = document.createElement("div");
  popup.className = "shift-revision-diff-popover";
  popup.id = `shift-revision-difference-${++nextDifferenceId}`;
  popup.setAttribute("popover", "auto");
  popup.role = "tooltip";
  trigger.setAttribute("aria-controls", popup.id);
  trigger.setAttribute("aria-describedby", popup.id);
  for (const [label, value] of [
    ["Bu sürümde", comparison.previousValue],
    ["Güncel sürümde", comparison.removed ? "Kaldırıldı" : comparison.currentValue],
  ]) {
    const row = document.createElement("div");
    const caption = document.createElement("strong");
    caption.textContent = label;
    const text = document.createElement("p");
    text.textContent = value;
    row.append(caption, text);
    popup.append(row);
  }
  host.classList.add("shift-revision-diff-host");
  host.append(trigger);
  root.append(popup);
  let closeTimer: ReturnType<typeof setTimeout> | undefined;
  const clearClose = () => clearTimeout(closeTimer);
  const close = () => {
    clearClose();
    popup.hidePopover();
  };
  const open = () => {
    clearClose();
    popup.showPopover();
    const anchor = trigger.getBoundingClientRect();
    const bounds = popup.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    popup.style.left = `${Math.max(12, Math.min(anchor.right - bounds.width, viewportWidth - bounds.width - 12))}px`;
    popup.style.top = `${Math.max(12, anchor.bottom + bounds.height + 10 < viewportHeight ? anchor.bottom + 8 : anchor.top - bounds.height - 8)}px`;
  };
  const scheduleClose = () => { closeTimer = setTimeout(close, 160); };
  const onPointerEnter = (event: PointerEvent) => { if (event.pointerType === "mouse") open(); };
  const onPointerLeave = (event: PointerEvent) => { if (event.pointerType === "mouse") scheduleClose(); };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (popup.matches(":popover-open")) close(); else open();
    }
  };
  const onToggle = () => trigger.setAttribute("aria-expanded", String(popup.matches(":popover-open")));
  trigger.addEventListener("pointerenter", onPointerEnter);
  trigger.addEventListener("pointerleave", onPointerLeave);
  trigger.addEventListener("focus", open);
  trigger.addEventListener("blur", close);
  trigger.addEventListener("click", open);
  trigger.addEventListener("keydown", onKeyDown);
  popup.addEventListener("pointerenter", clearClose);
  popup.addEventListener("pointerleave", scheduleClose);
  popup.addEventListener("toggle", onToggle);
  const scrollContainer = root.closest(".shift-approval-dialog-body");
  scrollContainer?.addEventListener("scroll", close, { passive: true });
  window.addEventListener("resize", close);
  return () => {
    clearClose();
    scrollContainer?.removeEventListener("scroll", close);
    window.removeEventListener("resize", close);
    trigger.remove();
    popup.remove();
    host.classList.remove("shift-revision-diff-host");
  };
}
